import {
  createFetchActionForNamespace,
  createPostActionForNamespace,
  createReducer,
  createFetchReducers,
  createFetchSelectors,
  createFetchReducersWithPagination,
  createFetchPaginatedActionForNamespace
} from 'utils/reducers';
import {transformPaginationForStoreToPaginationForApi} from 'utils/pagination';
import {transformFiltersForStoreToFiltersForApi} from 'utils/filters';
import {transformSortForStoreToSortForUrl} from 'utils/sort';
import {selectPagination, PAGINATION} from 'ducks/pagination';
import {selectFilters, FILTERS} from 'ducks/filters';
import {selectSort, SORTING} from 'ducks/sorting';

const createFetchAction = createFetchActionForNamespace('rooms');
const createPostAction = createPostActionForNamespace('rooms');
const createFetchPaginatedAction = createFetchPaginatedActionForNamespace(
  'rooms'
);

export const fetchRoomDetails = createFetchAction({
  name: 'FETCH_ROOM_DETAILS',
  initialAction: ({roomId}, extraActions = []) => ({
    payload: {roomId, extraActions}
  })
});

export const fetchRoomTypes = createFetchAction({
  name: 'FETCH_ROOM_TYPES',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {locationRef, extraActions}
  }),
  stateKey: 'roomTypes',
  defaultStateValue: []
});
export const selectRoomTypes = createFetchSelectors(fetchRoomTypes).data;

// We send only start hour because end hour is default booking end hour in the api
export const fetchAvailableRooms = createFetchAction({
  name: 'FETCH_AVAILABLE_ROOMS',
  initialAction: (
    {locationRef, roomType, startDate, startHour, endDate, cleanupPeriodDays},
    extraActions = []
  ) => ({
    payload: {
      locationRef,
      roomType,
      startDate,
      startHour,
      endDate,
      extraActions,
      cleanupPeriodDays
    }
  }),
  stateKey: 'availableRooms',
  defaultStateValue: []
});

export const fetchAvailableRoomsSelector = createFetchSelectors(
  fetchAvailableRooms
);
export const selectAvailableRooms = fetchAvailableRoomsSelector.data;
export const availableRoomsAreLoading = fetchAvailableRoomsSelector.isLoading;

export const fetchAvailableRoomsForBookingExtension = createFetchAction({
  name: 'FETCH_AVAILABLE_ROOMS_FOR_BOOKING_EXTENSION',
  initialAction: (
    {locationRef, roomType, extensionEnd, extensionCleanupPeriodDays, booking},
    extraActions = []
  ) => ({
    payload: {
      locationRef,
      roomType,
      extensionEnd,
      extensionCleanupPeriodDays,
      booking,
      extraActions
    }
  }),
  stateKey: 'availableRoomsForBookingExtension',
  defaultStateValue: []
});

export const fetchAvailableRoomsForBookingExtensionSelector = createFetchSelectors(
  fetchAvailableRoomsForBookingExtension
);
export const availableRoomsForBookingExtensionAreLoading =
  fetchAvailableRoomsForBookingExtensionSelector.isLoading;

export const fetchRoomOptions = createFetchAction({
  name: 'FETCH_ROOM_OPTIONS',
  initialAction: ({locationRef, roomType}, extraActions = []) => ({
    payload: {
      locationRef,
      roomType,
      extraActions
    }
  })
});

export const fetchArchivedRoomOptions = createFetchAction({
  name: 'FETCH_ARCHIVED_ROOM_OPTIONS',
  initialAction: ({locationRef, roomType}, extraActions = []) => ({
    payload: {
      locationRef,
      roomType,
      extraActions
    }
  })
});

export const fetchFloors = createFetchAction({
  name: 'FETCH_FLOORS',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {locationRef, extraActions}
  }),
  stateKey: 'floors',
  defaultStateValue: []
});
export const selectFloors = createFetchSelectors(fetchFloors).data;

// Non-paginated rooms for allocations calendar
export const fetchAllRooms = createFetchAction({
  name: 'FETCH_ALL_ROOMS',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {locationRef, extraActions}
  }),
  stateKey: 'allRooms',
  defaultStateValue: []
});
const allRoomsSelector = createFetchSelectors(fetchAllRooms);
export const selectAllRooms = allRoomsSelector.data;
export const selectAllRoomsLoading = allRoomsSelector.isLoading;

export const fetchRoomsAvailability = createFetchPaginatedAction({
  name: 'FETCH_ROOMS_AVAILABILITY',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {
      locationRef,
      extraActions
    }
  }),
  stateKey: 'availability',
  defaultStateValue: [],
  extraGetParamsSelectors: [
    state =>
      transformPaginationForStoreToPaginationForApi(
        selectPagination(state, PAGINATION.AVAILABILITY)
      ),
    state =>
      transformFiltersForStoreToFiltersForApi(
        selectFilters(state, FILTERS.AVAILABILITY)
      ),
    state =>
      transformSortForStoreToSortForUrl(
        selectSort(state, SORTING.AVAILABILITY_TABLE)
      )
  ],
  paginationNamespace: PAGINATION.AVAILABILITY
});
const roomsAvailabilitySelector = createFetchSelectors(fetchRoomsAvailability);
export const selectRoomsAvailability = roomsAvailabilitySelector.data;
export const selectRoomsAvailabilityLoading =
  roomsAvailabilitySelector.isLoading;

export const createRoom = createPostAction({
  name: 'CREATE_ROOM',
  initialAction: (
    {roomType, floor, roomNumber, creationDate, locationRef, dummy},
    extraActions
  ) => ({
    payload: {
      roomType,
      floor,
      roomNumber,
      creationDate,
      locationRef,
      dummy,
      extraActions
    }
  }),
  formName: 'createRoom'
});

export const archiveRoom = createPostAction({
  name: 'ARCHIVE_ROOM',
  initialAction: ({roomId, archiveDate, locationRef}, extraActions = []) => ({
    payload: {
      roomId,
      archiveDate,
      locationRef,
      extraActions
    }
  }),
  formName: 'archiveRoom'
});

export const undoArchiveRoom = createPostAction({
  name: 'UNDO_ARCHIVE_ROOM',
  initialAction: ({roomId, locationRef}, extraActions = []) => ({
    payload: {
      roomId,
      locationRef,
      extraActions
    }
  }),
  formName: 'undoArchiveRoom'
});

export const fetchRoomTypeFilters = createFetchAction({
  name: 'FETCH_ROOM_TYPE_FILTERS',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {
      locationRef,
      extraActions
    }
  }),
  stateKey: 'roomTypeFilters',
  defaultStateValue: []
});
export const selectRoomTypeFilters = createFetchSelectors(fetchRoomTypeFilters)
  .data;

export const fetchRoomNeighbours = createFetchAction({
  name: 'FETCH_ROOM_NEIGHBOURS',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {
      locationRef,
      extraActions
    }
  }),
  stateKey: 'roomNeighbours',
  defaultStateValue: {}
});
export const selectRoomNeighbours = createFetchSelectors(fetchRoomNeighbours)
  .data;

const fetchRoomNeighboursReducer = createFetchReducers(
  fetchRoomNeighbours,
  (state, action) => ({
    ...state,
    [action.parent.stateKey]: {
      isLoading: false,
      data: action.payload.reduce(
        (result, room) => ({
          ...result,
          [room.id]: room.neighbour
        }),
        {}
      )
    }
  })
);

const initialState = {};

const fetchReducers = createReducer(initialState, {
  ...createFetchReducers(fetchRoomTypes),
  ...createFetchReducers(fetchAvailableRooms),
  ...createFetchReducers(fetchAvailableRoomsForBookingExtension),
  ...createFetchReducers(fetchFloors),
  ...createFetchReducers(fetchAllRooms),
  ...createFetchReducers(fetchRoomTypeFilters),
  ...createFetchReducersWithPagination(fetchRoomsAvailability),
  ...fetchRoomNeighboursReducer
});

const reducer = (state = initialState, action) => {
  const newState = fetchReducers(state, action);

  switch (action.type) {
    default:
      return newState;
  }
};

export default reducer;
export {default as roomsSaga} from './saga';
export * from './selectors';
